import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import get from 'lodash.get';

import RecognizedIcon from 'dibs-vg/dist/react/recognized-ribbon';
import TopSellerIcon from 'dibs-vg/dist/react/diamond';
import DiamondFilled from 'dibs-vg/dist/react/diamond-filled';
import AssociationsIcon from 'dibs-vg/dist/react/leaf-crown';
import { SellerReviewsModalAsync } from 'dibs-seller-reviews-modal/exports/SellerReviewsModalAsync';

import {
    recognizedTitle,
    recognizedDescription,
    goldSellerTitle,
    platinumSellerTitle,
    diamondSellerTitle,
    tieredSellerDescription,
    associationsTitle,
    associationsDescription,
} from '../messages/pdpDistinctionsMessages';
import { getIsNewSeller } from '../../utils/sellerHelpers';

import { PdpSharedStarRating } from '../PdpSharedStarRating/PdpSharedStarRating';
import { PdpSharedNewSellerPill } from '../PdpSharedNewSellerPill/PdpSharedNewSellerPill';
import { PdpRespSidebarSellerInfoTitle } from './PdpRespSidebarSellerInfoTitle';
import { PdpRespSidebarSellerInfoIcon } from './PdpRespSidebarSellerInfoIcon';

import styles from './PdpRespSidebarSellerInfo.scss';

class PdpRespSidebarSellerInfoComponent extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isReviewModalOpen: false,
            load: false,
            isVisible: false,
        };

        this.openModal = this.openModal.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    openModal() {
        this.setState({ isReviewModalOpen: true });
    }

    onClose() {
        this.setState({ isReviewModalOpen: false });
    }

    render() {
        const { seller, relay, viewer, user } = this.props;
        const { isReviewModalOpen } = this.state;

        // Seller icons
        const isRecognized = get(seller, 'isDistinguished');
        const sellerTierDataFound = !get(seller, 'sellerTiers.sellerTierDataNotFound');
        const isGoldSeller = sellerTierDataFound && get(seller, 'sellerTiers.isGoldTier');
        const isPlatinumSeller = sellerTierDataFound && get(seller, 'sellerTiers.isPlatinumTier');
        const isRewardsTier2 = get(seller, 'isRewardsTier2');
        const sellerId = get(seller, 'serviceId');
        const tradeAssociations = get(seller, 'tradeAssociations') || [];
        const isReviewsEnabled = get(viewer, 'isReviewsEnabled');

        const isTopSeller = isGoldSeller || isPlatinumSeller || isRewardsTier2;
        let topSellerTitle = '';
        let customIconStyles = '';
        if (isRewardsTier2) {
            topSellerTitle = diamondSellerTitle;
        } else if (isGoldSeller) {
            topSellerTitle = goldSellerTitle;
            customIconStyles = styles.goldDiamond;
        } else if (isPlatinumSeller) {
            topSellerTitle = platinumSellerTitle;
            customIconStyles = styles.platinumDiamond;
        }
        const isVerifiedTrade = get(user, 'isVerifiedTrade');
        const canShowSellerName = get(user, 'canShowSellerName');
        const sellerCompany =
            (isVerifiedTrade && canShowSellerName && get(seller, 'sellerProfile.company')) || '';

        // Seller Since
        const monthsSinceStartDate = get(seller, 'monthsSinceStartDate');
        const isNewSeller = getIsNewSeller(monthsSinceStartDate);
        const isNewSellerPillEnabled = true;

        // Seller rating
        const reviews = get(seller, 'orderFeedbackPdp') || null;
        let feedbackRating = get(reviews, 'metadata.aggregatedFeedback.overallExperience.mean');
        feedbackRating = feedbackRating ? parseFloat(feedbackRating.toFixed(1)) : 0;

        const starRating = <PdpSharedStarRating feedbackRating={feedbackRating} />;

        const distinction =
            feedbackRating === 0 && isNewSeller && isNewSellerPillEnabled ? (
                <PdpSharedNewSellerPill />
            ) : (
                <React.Fragment>
                    {isTopSeller && (
                        <PdpRespSidebarSellerInfoIcon
                            title={topSellerTitle}
                            description={tieredSellerDescription}
                            icon={isRewardsTier2 ? DiamondFilled : TopSellerIcon}
                            dataTn="pdp-sidebar-seller-top-tier"
                            customIconStyles={customIconStyles}
                        />
                    )}
                    {isRecognized && (
                        <PdpRespSidebarSellerInfoIcon
                            title={recognizedTitle}
                            description={recognizedDescription}
                            icon={RecognizedIcon}
                            dataTn="pdp-sidebar-seller-recognized"
                        />
                    )}
                    {tradeAssociations.length ? (
                        <PdpRespSidebarSellerInfoIcon
                            title={associationsTitle}
                            description={associationsDescription}
                            icon={AssociationsIcon}
                            dataTn="pdp-sidebar-seller-associations"
                        />
                    ) : null}
                </React.Fragment>
            );

        const feedbackElement =
            feedbackRating > 0 ? (
                <React.Fragment>
                    {isReviewsEnabled ? (
                        <a
                            href="#0"
                            className={styles.ratingWrapper}
                            onClick={this.openModal}
                            data-tn="pdp-sidebar-seller-rating-link"
                        >
                            {starRating}
                        </a>
                    ) : (
                        <div data-tn="pdp-sidebar-seller-rating-link">{starRating}</div>
                    )}
                    {sellerId && (
                        <SellerReviewsModalAsync
                            environment={relay.environment}
                            isOpen={isReviewModalOpen}
                            onClose={this.onClose}
                            sellerId={sellerId}
                        />
                    )}
                </React.Fragment>
            ) : (
                starRating
            );

        return (
            <div className={styles.container} data-tn="pdp-sidebar-seller-info">
                <div className={styles.wrapper}>
                    <PdpRespSidebarSellerInfoTitle sellerCompany={sellerCompany} />
                    {distinction}
                </div>
                {feedbackElement}
            </div>
        );
    }
}

PdpRespSidebarSellerInfoComponent.propTypes = {
    seller: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    user: PropTypes.object,
    relay: PropTypes.object,
    vipUserRelationship: PropTypes.object,
};

export const PdpRespSidebarSellerInfo = createFragmentContainer(PdpRespSidebarSellerInfoComponent, {
    viewer: graphql`
        fragment PdpRespSidebarSellerInfo_viewer on Viewer {
            isReviewsEnabled: isEnabledByLocale(featureName: "ORDER_FEEDBACK")
        }
    `,
    user: graphql`
        fragment PdpRespSidebarSellerInfo_user on User {
            isVerifiedTrade
            canShowSellerName
        }
    `,
    seller: graphql`
        fragment PdpRespSidebarSellerInfo_seller on Seller {
            monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
            serviceId
            isDistinguished
            isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
            sellerTiers {
                isGoldTier
                isPlatinumTier
                sellerTierDataNotFound
            }
            orderFeedbackPdp {
                metadata {
                    aggregatedFeedback {
                        overallExperience {
                            mean
                        }
                    }
                }
            }
            sellerProfile {
                company
            }
            tradeAssociations {
                serviceId
            }
        }
    `,
});
